/**
 * AddPieceDialog — P0 SPRINT (Feb 2026) · PERMANENT SHIPMENT ACCESS
 *
 * Voegt een extra colli/doos toe aan een bestaande zending zonder een
 * nieuw W-nummer of shipment record aan te maken.
 *
 * Consolidatie-impact: als het shipment al gekoppeld is aan een
 * consolidatie, retourneert de backend een 409 met impact-details.
 * Dit dialog toont die impact en vraagt expliciete bevestiging voordat
 * de operatie wordt herhaald met `acknowledge_consolidation_impact=true`.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, AlertTriangle, Package } from "lucide-react";

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";

import { adminApi, formatApiError } from "../../../context/AuthContext";
import { useGlobalLoading } from "../../../context/GlobalLoadingProvider";

const EMPTY = {
  quantity: "1",
  weight_kg: "",
  length_cm: "",
  width_cm: "",
  height_cm: "",
  contents_description: "",
  value: "",
  currency: "EUR",
  package_type: "",
  internal_note: "",
};

export default function AddPieceDialog({ open, onClose, shipment, onAdded }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [pendingImpact, setPendingImpact] = useState(null);
  const [ackOpen, setAckOpen] = useState(false);
  const { runTask } = useGlobalLoading();

  const bind = (k) => ({
    value: form[k] || "",
    onChange: (e) => setForm((f) => ({ ...f, [k]: e.target.value })),
  });

  const buildPayload = (ack = false) => ({
    quantity: Number(form.quantity || 0),          // 0 blijft 0
    weight_kg: form.weight_kg ? Number(form.weight_kg) : null,
    length_cm: form.length_cm ? Number(form.length_cm) : null,
    width_cm:  form.width_cm  ? Number(form.width_cm)  : null,
    height_cm: form.height_cm ? Number(form.height_cm) : null,
    contents_description: form.contents_description || null,
    value: form.value ? Number(form.value) : null,
    currency: form.currency || null,
    package_type: form.package_type || null,
    internal_note: form.internal_note || null,
    acknowledge_consolidation_impact: ack,
  });

  const submit = async (ack = false) => {
    if (Number(form.quantity) < 0) {
      toast.error("Aantal mag niet negatief zijn.");
      return;
    }
    setBusy(true);
    try {
      const res = await runTask({
        id: `add-piece-${shipment.id}`,
        message: "Extra doos wordt toegevoegd…",
        successMessage: "Colli toegevoegd",
        errorMessage: "Toevoegen mislukt",
        run: () => adminApi.post(`/admin/shipping-labels/${shipment.id}/pieces`, buildPayload(ack)),
      });
      if (onAdded) onAdded(res.data);
      onClose?.();
      setForm(EMPTY);
    } catch (err) {
      // Consolidation-impact 409 → toon impact modal
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail?.error === "consolidation_impact_confirmation_required") {
        setPendingImpact(detail);
        setAckOpen(true);
        return;
      }
      toast.error(formatApiError(err) || "Toevoegen mislukt");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose?.(); }}>
        <DialogContent className="max-w-2xl" data-testid="add-piece-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-chivo font-black text-[#0A192F]">
              <Package className="w-5 h-5" /> Extra colli toevoegen
            </DialogTitle>
            <DialogDescription>
              Voeg een extra doos toe aan zending{" "}
              <span className="font-mono font-bold">{shipment?.tracking_number}</span>.
              Het W-nummer blijft gelijk — totalen worden automatisch herberekend.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Aantal *</Label>
              <Input {...bind("quantity")} type="number" min="0" data-testid="add-piece-quantity" />
              <p className="text-[10px] text-gray-500 mt-1">0 blijft 0 (geen automatische promotie naar 1).</p>
            </div>
            <div>
              <Label className="text-xs">Verpakking</Label>
              <Input {...bind("package_type")} placeholder="Doos / Barrel / Koffer / Zak / Pallet"
                     data-testid="add-piece-package-type" />
            </div>
            <div>
              <Label className="text-xs">Gewicht per stuk (kg)</Label>
              <Input {...bind("weight_kg")} type="number" step="0.01" data-testid="add-piece-weight" />
            </div>
            <div>
              <Label className="text-xs">Waarde per stuk</Label>
              <Input {...bind("value")} type="number" step="0.01" data-testid="add-piece-value" />
            </div>
            <div>
              <Label className="text-xs">Lengte (cm)</Label>
              <Input {...bind("length_cm")} type="number" step="0.1" data-testid="add-piece-length" />
            </div>
            <div>
              <Label className="text-xs">Breedte (cm)</Label>
              <Input {...bind("width_cm")} type="number" step="0.1" data-testid="add-piece-width" />
            </div>
            <div>
              <Label className="text-xs">Hoogte (cm)</Label>
              <Input {...bind("height_cm")} type="number" step="0.1" data-testid="add-piece-height" />
            </div>
            <div>
              <Label className="text-xs">Valuta</Label>
              <Input {...bind("currency")} placeholder="EUR / USD / ANG" data-testid="add-piece-currency" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Omschrijving inhoud</Label>
              <Input {...bind("contents_description")} placeholder="Bijv. persoonlijke effecten"
                     data-testid="add-piece-contents" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Interne notitie</Label>
              <Textarea {...bind("internal_note")} rows={2}
                        placeholder="Bijv. door wie ontvangen, waar in het magazijn, foto's..."
                        data-testid="add-piece-note" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={busy}
                    data-testid="add-piece-cancel">Annuleren</Button>
            <Button onClick={() => submit(false)} disabled={busy}
                    className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
                    data-testid="add-piece-submit">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Colli toevoegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Consolidation impact confirmation dialog */}
      <Dialog open={ackOpen} onOpenChange={(v) => { if (!v && !busy) setAckOpen(false); }}>
        <DialogContent className="max-w-lg" data-testid="add-piece-impact-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="w-5 h-5" /> Consolidatie-impact bevestigen
            </DialogTitle>
            <DialogDescription>
              {pendingImpact?.message}
            </DialogDescription>
          </DialogHeader>

          {pendingImpact?.consolidation && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs space-y-1">
              <div><span className="font-bold">Consolidatie:</span>{" "}
                <span className="font-mono">{pendingImpact.consolidation.consolidation_number}</span>
              </div>
              {pendingImpact.consolidation.status && (
                <div><span className="font-bold">Status:</span> {pendingImpact.consolidation.status}</div>
              )}
              {pendingImpact.consolidation.destination && (
                <div><span className="font-bold">Bestemming:</span> {pendingImpact.consolidation.destination}</div>
              )}
              {pendingImpact.consolidation.etd && (
                <div><span className="font-bold">ETD:</span> {pendingImpact.consolidation.etd}</div>
              )}
              {pendingImpact.consolidation.eta && (
                <div><span className="font-bold">ETA:</span> {pendingImpact.consolidation.eta}</div>
              )}
              <div className="pt-2 border-t border-amber-200 mt-2">
                <div className="font-bold text-amber-900">Huidige totalen:</div>
                <div>Colli: {pendingImpact.current_totals?.piece_count ?? "—"}</div>
                <div>Gewicht: {pendingImpact.current_totals?.weight_kg ?? "—"} kg</div>
                <div>CBM: {pendingImpact.current_totals?.cbm_m3 ?? "—"} m³</div>
              </div>
              <div className="pt-2 text-amber-900 font-semibold">
                Bevestig om door te gaan — het manifest kan een nieuwe versie vereisen.
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setAckOpen(false)} disabled={busy}
                    data-testid="add-piece-impact-cancel">Annuleren</Button>
            <Button onClick={async () => { setAckOpen(false); await submit(true); }}
                    disabled={busy}
                    className="bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid="add-piece-impact-confirm">
              Bevestigen en toevoegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
